import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Card, date, ui } from '@/components/ui';
import { useBusiness } from '@/features/business/BusinessProvider';
import { useTenantKey } from '@/features/business/useTenantKey';
import { businessEnforcementService, type EnforcementNotice } from '@/services/api';

const LABELS: Record<string, string> = {
  INVITATIONS: 'review invitations',
  API: 'the API, WordPress plugin and webhooks',
  WIDGETS: 'review widgets on your website',
  REPLIES: 'replying to reviews',
};

// Wraps every dashboard page. Shows Fairvero's notices (warnings, restrictions) as banners, and
// while the business is suspended replaces the page with an explanation, except the Help page,
// which stays open so the owner can appeal. The API enforces all of this; this only explains it.
export function EnforcementGuard({ children }: { children: ReactNode }) {
  const { context } = useBusiness();
  const key = useTenantKey();
  const { pathname } = useLocation();
  const q = useQuery({
    queryKey: key('enforcement'),
    queryFn: businessEnforcementService.state,
    enabled: Boolean(context),
    staleTime: 60_000,
  });
  const state = q.data;
  if (!state) return <>{children}</>;

  if (state.suspended && pathname !== '/help') return <Suspended notices={state.notices} />;

  return (
    <>
      {state.notices
        .filter((n) => n.action !== 'SUSPEND')
        .map((n) => (
          <Banner key={n.id} notice={n} />
        ))}
      {children}
    </>
  );
}

function Banner({ notice }: { notice: EnforcementNotice }) {
  const restricted = notice.action === 'RESTRICT';
  return (
    <div className={ui.banner} role="status">
      <strong>{restricted ? 'Restricted by Fairvero: ' : 'Notice from Fairvero: '}</strong>
      {restricted
        ? `${notice.restrictions.map((r) => LABELS[r] ?? r).join(', ')} unavailable`
        : null}
      {restricted && notice.endsAt ? ` until ${date(notice.endsAt)}` : null}
      {restricted ? '. ' : null}
      <span style={{ whiteSpace: 'pre-line' }}>{notice.message}</span>{' '}
      <Link to="/help">Questions or appeal</Link>
    </div>
  );
}

function Suspended({ notices }: { notices: EnforcementNotice[] }) {
  const suspension = notices.find((n) => n.action === 'SUSPEND');
  return (
    <Card title="This business is suspended" highlight>
      <div className={ui.stack}>
        <p>
          Fairvero suspended this business
          {suspension ? ` on ${date(suspension.since)}` : ''}
          {suspension?.endsAt ? ` until ${date(suspension.endsAt)}` : ''}. While suspended, its
          public page, review widgets, invitations, API and WordPress plugin are switched off, and
          this dashboard is read-only.
        </p>
        {suspension ? (
          <div>
            <strong>Message from Fairvero</strong>
            <p style={{ whiteSpace: 'pre-line' }}>{suspension.message}</p>
          </div>
        ) : null}
        <p>
          If you think this is a mistake, write to us from the Help page. A different member of our
          team will look at it.
        </p>
        <p>
          <Link to="/help">Go to Help and appeal</Link>
        </p>
      </div>
    </Card>
  );
}
